import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCcw, AlertTriangle, ChevronDown, ChevronUp, Lock, Package, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { api, pollRefresh } from '../lib/api';
import { formatINR } from '../lib/formatINR';
import { GlassCard } from './GlassCard';
import Drawer from './Drawer';

/* ── severity colors ── */
const SEV = {
  critical: { bg: 'bg-red-500/15', text: 'text-red-400', rail: 'border-l-red-500' },
  high:     { bg: 'bg-red-500/15', text: 'text-red-400', rail: 'border-l-red-500' },
  warn:     { bg: 'bg-orange-500/15', text: 'text-orange-400', rail: 'border-l-orange-500' },
  medium:   { bg: 'bg-yellow-500/15', text: 'text-yellow-400', rail: 'border-l-yellow-500' },
  low:      { bg: 'bg-emerald-500/15', text: 'text-emerald-400', rail: 'border-l-emerald-500' },
  info:     { bg: 'bg-emerald-500/15', text: 'text-emerald-400', rail: 'border-l-emerald-500' },
};

function relTime(iso) {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.max(0, Math.round(ms / 60000));
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    return `${Math.round(m / 60)}h ago`;
  } catch { return ''; }
}

/* ── Main export ── */
export default function InventoryIntelPanel() {
  const { token, hasPlan, planState } = useAuth();
  const isPremium = hasPlan('premium') || planState?.effective_plan === 'trial';
  const toast = useToast();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const genRef = useRef(null);

  /* drawer state */
  const [drawer, setDrawer] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [approved, setApproved] = useState(null);

  /* collapsible state */
  const [deadOpen, setDeadOpen] = useState(false);
  const [slowOpen, setSlowOpen] = useState(false);

  /* fetch on mount (premium only) */
  const load = useCallback(async () => {
    if (!token || !isPremium) return;
    setLoading(true);
    try {
      const r = await api.inventoryIntelligence(token);
      setData(r);
      genRef.current = r?.generated_at || null;
    } catch (e) {
      if (e?.status === 403) return; /* premium_required fallback handled by isPremium */
      if (e?.status !== 401) toast.show(e?.message || 'Failed to load intelligence', 'error');
    } finally { setLoading(false); }
  }, [token, isPremium, toast]);

  useEffect(() => { load(); }, [load]);

  /* refresh with pollRefresh */
  const doRefresh = useCallback(async () => {
    if (!token || refreshing) return;
    setRefreshing(true);
    const prev = genRef.current;
    try {
      const r = await pollRefresh({
        postFn: () => api.inventoryIntelligenceRefresh(token),
        getFn: () => api.inventoryIntelligence(token),
        prevGeneratedAt: prev,
        pollIntervalMs: 5000,
        maxWaitMs: 130000,
      });
      setData(r);
      genRef.current = r?.generated_at || null;
      toast.show('Inventory intelligence refreshed', 'success');
    } catch (e) {
      if (e?.status === 429) toast.show('Rate limited — try again later', 'error');
      else toast.show(e?.message || 'Refresh failed', 'error');
    } finally { setRefreshing(false); }
  }, [token, refreshing, toast]);

  /* action engine */
  const handleApprove = async () => {
    if (!drawer || actionBusy) return;
    setActionBusy(true);
    try {
      const proposed = await api.proposeAction(token, {
        type: drawer.type,
        payload: drawer.payload,
        source_insight_id: drawer.insightId || undefined,
      });
      await api.approveAction(token, proposed.id);
      setApproved({ id: proposed.id, kind: drawer.type });
      toast.show(drawer.type === 'restock_po' ? 'Reorder draft created' : 'Action approved', 'success');
      setDrawer(null);
    } catch (e) { toast.show(e?.message || 'Action failed', 'error'); }
    finally { setActionBusy(false); }
  };

  /* ── FREE / BASIC upsell ── */
  if (!isPremium) {
    return (
      <div className="mb-8 relative" data-testid="intel-upsell-panel">
        <GlassCard dark className="p-6 overflow-hidden">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={20} className="text-purple-400" />
            <h2 className="text-lg font-extrabold text-white">AI Inventory Intelligence</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 filter blur-[8px] pointer-events-none select-none" aria-hidden>
            {[1,2,3].map(i => (
              <div key={i} className="glass-card p-5 rounded-2xl">
                <div className="h-3 w-20 bg-white/10 rounded mb-3" />
                <div className="h-6 w-28 bg-white/10 rounded" />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <button
              data-testid="intel-upsell-cta"
              onClick={() => navigate('/billing')}
              className="flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-teal-500 text-white font-extrabold text-sm shadow-2xl hover:shadow-purple-500/30 hover:scale-[1.02] transition-all"
            >
              <Lock size={16} /> Unlock AI Strategy
            </button>
          </div>
        </GlassCard>
      </div>
    );
  }

  /* ── PREMIUM content ── */
  const totals = data?.totals || {};
  const reorders = data?.reorder_alerts || [];
  const recs = data?.ai_recommendations || [];
  const dead = data?.dead_stock || [];
  const slow = data?.slow_moving || [];
  const skuCounts = totals.sku_counts || {};
  const rel = relTime(data?.generated_at);
  const source = data?.source || (data?.cached ? 'cache' : '');
  const deadPct = totals.inventory_value_rupees > 0 ? (totals.dead_stock_value_rupees / totals.inventory_value_rupees) * 100 : 0;

  return (
    <div className="mb-8 space-y-4" data-testid="intel-panel">
      {/* ── Header ── */}
      <GlassCard dark className="p-4 flex items-center gap-3" testId="intel-header">
        <Sparkles size={20} className="text-purple-400 flex-shrink-0" />
        <div className="flex-1">
          <h2 className="text-white font-extrabold text-base">AI Inventory Intelligence</h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {rel && <span className="text-white/40 text-xs" data-testid="intel-updated">Updated {rel}</span>}
            {source && <span className="px-1.5 py-0.5 rounded bg-white/8 text-white/50 text-[10px] font-bold uppercase tracking-wider" data-testid="intel-source">{source}</span>}
          </div>
        </div>
        <button onClick={doRefresh} disabled={refreshing || loading} data-testid="intel-refresh-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-full bg-white text-navy-900 text-xs font-bold hover:bg-white/90 transition-colors disabled:opacity-50">
          {refreshing ? <div className="w-3 h-3 border-2 border-navy-900 border-t-transparent rounded-full animate-spin" /> : <RefreshCcw size={12} />}
          Refresh
        </button>
      </GlassCard>

      {loading && !data ? (
        <div className="py-8 flex justify-center"><div className="w-5 h-5 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" /></div>
      ) : data ? (
        <>
          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <GlassCard dark className="p-5" testId="intel-kpi-total">
              <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">Total Inventory Value</p>
              <p className="text-white font-extrabold text-2xl mt-1 tabular-nums">{totals.inventory_value_rupees == null ? 'Not available from ERP' : formatINR(totals.inventory_value_rupees)}</p>
            </GlassCard>
            <GlassCard dark className={`p-5 ${deadPct > 10 ? 'border border-orange-500/30' : ''}`} testId="intel-kpi-dead">
              <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">Working Capital in Dead Stock</p>
              <p className={`font-extrabold text-2xl mt-1 tabular-nums ${deadPct > 10 ? 'text-orange-400' : 'text-white'}`}>
                {totals.dead_stock_value_rupees == null ? 'Not available from ERP' : formatINR(totals.dead_stock_value_rupees)}
              </p>
              {deadPct > 10 && <p className="text-orange-400/70 text-xs mt-1">{deadPct.toFixed(1)}% of total inventory</p>}
            </GlassCard>
          </div>

          {/* ── SKU Velocity Bar ── */}
          <SkuVelocityBar counts={skuCounts} />

          {/* ── Approved banner ── */}
          {approved && (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="intel-approved-banner">
              <p className="text-emerald-400 font-bold text-sm">{approved.kind === 'restock_po' ? 'PO created' : 'Action created'}</p>
              <p className="text-emerald-400/60 text-xs mt-1">{approved.kind.replace(/_/g, ' ')} · {approved.id?.slice(0, 8)}</p>
            </div>
          )}

          {/* ── Reorder Alerts ── */}
          {reorders.length > 0 && (
            <div>
              <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <AlertTriangle size={14} className="text-orange-400" /> Reorder Alerts ({reorders.length})
              </h3>
              <div className="space-y-3">
                {reorders.map((r, i) => {
                  const stockoutDate = r.predicted_stockout_date ? new Date(r.predicted_stockout_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }) : null;
                  return (
                    <GlassCard dark key={r.product_id || i} className="p-4 border-l-4 border-l-orange-500" testId={`intel-reorder-card-${r.product_id || i}`}>
                      <p className="text-white font-bold text-sm">{r.product_name}</p>
                      <p className="text-white/50 text-xs mt-1">
                        {r.days_of_cover != null ? `${Math.round(r.days_of_cover)}d cover` : ''}{stockoutDate ? ` · stockout ${stockoutDate}` : ''}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-white/40 text-xs">
                        <span>On hand: <span className="text-white font-semibold">{r.on_hand_qty}</span></span>
                        <span>Avg/day: <span className="text-white font-semibold">{r.avg_daily_sales?.toFixed(1)}</span></span>
                        <span>Suggested: <span className="text-teal-400 font-semibold">{r.suggested_reorder_qty}</span></span>
                      </div>
                      <button
                        data-testid={`intel-reorder-btn-${r.product_id || i}`}
                        onClick={() => setDrawer({
                          title: `Reorder — ${r.product_name}`,
                          type: 'restock_po',
                          payload: { product_id: r.product_id, product_name: r.product_name, suggested_qty: r.suggested_reorder_qty, cost_per_unit: r.cost_per_unit },
                        })}
                        className="mt-3 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-xs font-bold hover:shadow-lg hover:shadow-teal-500/20 transition-all"
                      >
                        Prepare Reorder
                      </button>
                    </GlassCard>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── AI Recommendations ── */}
          {recs.length > 0 && (
            <div>
              <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">AI Recommendations</h3>
              <div className="space-y-3">
                {recs.map((r, i) => {
                  const sev = SEV[r.severity] || SEV.info;
                  const showAction = r?.action_engine?.type && r.action_engine.type !== 'none';
                  return (
                    <GlassCard dark key={r.id || i} className={`p-4 border-l-4 ${sev.rail}`} testId={`intel-rec-${r.id || i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${sev.bg} ${sev.text}`}>{(r.severity || 'info').toUpperCase()}</span>
                        {r.category && <span className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 text-[10px] font-extrabold uppercase">{r.category}</span>}
                      </div>
                      <p className="text-white font-bold text-sm">{r.title}</p>
                      {r.why && <p className="text-white/60 text-xs mt-1">{r.why}</p>}
                      {r.impact_estimate && <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-white/8 text-white/50 text-xs font-bold">Impact · {r.impact_estimate}</span>}
                      {showAction && (
                        <button
                          data-testid={`intel-rec-take-${r.id || i}`}
                          onClick={() => setDrawer({
                            title: `Action — ${r.title?.slice(0, 40)}`,
                            type: r.action_engine.type,
                            payload: r.action_engine.payload || {},
                            insightId: r.id,
                          })}
                          className="mt-3 px-4 py-2.5 rounded-full bg-white text-navy-900 text-xs font-bold hover:bg-white/90 transition-colors"
                        >
                          Take Action · {r.action_engine.type.replace(/_/g, ' ')}
                        </button>
                      )}
                    </GlassCard>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Dead Stock (collapsible) ── */}
          {dead.length > 0 && (
            <CollapsibleSection title={`Dead Stock (${dead.length})`} open={deadOpen} onToggle={() => setDeadOpen(o => !o)}
              extra={
                <button
                  data-testid="intel-suggest-clearance-btn"
                  onClick={() => {
                    const top = dead[0];
                    setDrawer({
                      title: `Suggest Clearance — ${top.product_name}`,
                      type: 'price_change',
                      payload: { product_id: top.product_id, product_name: top.product_name, current_value: top.cost_value_rupees },
                    });
                  }}
                  className="px-3 py-1.5 rounded-full bg-orange-500/15 text-orange-400 text-[11px] font-bold hover:bg-orange-500/25 transition-colors"
                >
                  Suggest Clearance
                </button>
              }
            >
              <div className="space-y-2">
                {dead.map((d, i) => (
                  <div key={d.product_id || i} className="flex items-center justify-between py-2 px-1 border-b border-white/5 last:border-0" data-testid={`intel-dead-${d.product_id || i}`}>
                    <div>
                      <p className="text-white text-sm font-semibold">{d.product_name}</p>
                      <p className="text-white/40 text-xs">Last sold: {d.last_sale_date ? new Date(d.last_sale_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: '2-digit' }) : 'Never'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/70 text-sm tabular-nums">{d.on_hand_qty} units</p>
                      <p className="text-orange-400 text-xs tabular-nums">{formatINR(d.cost_value_rupees)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* ── Slow Moving (collapsible) ── */}
          {slow.length > 0 && (
            <CollapsibleSection title={`Slow Moving (${slow.length})`} open={slowOpen} onToggle={() => setSlowOpen(o => !o)}>
              <div className="space-y-2">
                {slow.map((s, i) => (
                  <div key={s.product_id || i} className="flex items-center justify-between py-2 px-1 border-b border-white/5 last:border-0" data-testid={`intel-slow-${s.product_id || i}`}>
                    <div>
                      <p className="text-white text-sm font-semibold">{s.product_name}</p>
                      <p className="text-white/40 text-xs">Last sold: {s.last_sale_date ? new Date(s.last_sale_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: '2-digit' }) : 'Never'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/70 text-sm tabular-nums">{s.on_hand_qty} units</p>
                      <p className="text-yellow-400 text-xs tabular-nums">{formatINR(s.cost_value_rupees)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}
        </>
      ) : null}

      {/* ── Drawer ── */}
      <Drawer open={!!drawer} onClose={() => setDrawer(null)} title={drawer?.title || 'Action'}>
        {drawer && (
          <div className="space-y-4">
            <div className="glass-card p-4">
              <p className="text-white/50 text-xs uppercase font-semibold tracking-wider mb-2">Type</p>
              <p className="text-white font-bold text-sm">{drawer.type?.replace(/_/g, ' ')}</p>
            </div>
            {Object.entries(drawer.payload || {}).map(([k, v]) => (
              <div key={k} className="glass-card p-4">
                <p className="text-white/50 text-xs uppercase font-semibold tracking-wider mb-1">{k.replace(/_/g, ' ')}</p>
                <p className="text-white text-sm font-semibold tabular-nums">{typeof v === 'number' ? formatINR(v) : String(v)}</p>
              </div>
            ))}
            <div className="flex gap-3 mt-6">
              <button data-testid="sheet-approve" onClick={handleApprove} disabled={actionBusy}
                className="flex-1 h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50">
                {actionBusy ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Approve'}
              </button>
              <button data-testid="sheet-dismiss" onClick={() => setDrawer(null)}
                className="flex-1 h-12 rounded-xl bg-white/5 border border-white/10 text-white/60 font-bold text-sm hover:bg-white/10 transition-colors">
                Dismiss
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

/* ── SKU Velocity Stacked Bar ── */
function SkuVelocityBar({ counts }) {
  const fast = counts.fast || 0;
  const normal = counts.normal || 0;
  const slow = counts.slow || 0;
  const dead = counts.dead || 0;
  const total = fast + normal + slow + dead;
  if (total === 0) return null;

  const segments = [
    { label: 'Fast', count: fast, color: 'bg-emerald-500', text: 'text-emerald-400' },
    { label: 'Normal', count: normal, color: 'bg-blue-500', text: 'text-blue-400' },
    { label: 'Slow', count: slow, color: 'bg-yellow-500', text: 'text-yellow-400' },
    { label: 'Dead', count: dead, color: 'bg-red-500', text: 'text-red-400' },
  ];

  return (
    <GlassCard dark className="p-5" testId="intel-sku-velocity">
      <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider mb-3">SKU Velocity Distribution</p>
      <div className="flex h-6 rounded-full overflow-hidden bg-white/5">
        {segments.map(s => s.count > 0 && (
          <div key={s.label} className={`${s.color} transition-all`} style={{ width: `${(s.count / total) * 100}%` }}
            title={`${s.label}: ${s.count}`} />
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 flex-wrap">
        {segments.map(s => (
          <span key={s.label} className="flex items-center gap-1.5 text-xs">
            <span className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
            <span className="text-white/50">{s.label}</span>
            <span className={`font-bold tabular-nums ${s.text}`}>{s.count}</span>
          </span>
        ))}
      </div>
    </GlassCard>
  );
}

/* ── Collapsible section ── */
function CollapsibleSection({ title, open, onToggle, extra, children }) {
  return (
    <GlassCard dark className="overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors" data-testid={`collapse-${title.split(' ')[0].toLowerCase()}`}>
        <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider">{title}</h3>
        <div className="flex items-center gap-2">
          {extra}
          {open ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
        </div>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </GlassCard>
  );
}
